import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, X, AlertCircle, Search, CheckCircle, XCircle, User, FileText, Wrench, DollarSign, Clock, ThumbsUp, ThumbsDown, Hash, MessageSquare, Camera, MapPin, CheckSquare, Calendar, BarChart3 } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import { getTickets, createTicket, updateTicket, deleteTicket, approveTicket, prosesTicket, selesaiTicket, respondTicket, getTicketingVehicles, getTicketingStats } from '../api';
import { useAuth } from '../AuthContext';

const STATUS_OPTIONS = ['Menunggu', 'Disetujui', 'Dalam Perbaikan', 'Selesai', 'Ditolak'];
const PRIORITAS_OPTIONS = ['Rendah', 'Normal', 'Tinggi', 'Darurat'];
const KATEGORI_OPTIONS = ['Lampu', 'Mesin', 'AC', 'Body', 'Elektrik', 'Ban', 'Rem', 'Transmisi', 'Lainnya'];

const statusConfig = {
  'Menunggu': { color: '#fb6340', bg: '#fff5ee' },
  'Disetujui': { color: '#11cdef', bg: '#eef9fd' },
  'Dalam Perbaikan': { color: '#8965e0', bg: '#f3edff' },
  'Selesai': { color: '#2dce89', bg: '#e8f8f0' },
  'Ditolak': { color: '#8898aa', bg: '#f6f8fa' },
};

const prioritasConfig = {
  'Rendah': { color: '#2dce89', bg: '#e8f8f0' },
  'Normal': { color: '#11cdef', bg: '#eef9fd' },
  'Tinggi': { color: '#fb6340', bg: '#fff5ee' },
  'Darurat': { color: '#f5365c', bg: '#fff5f5' },
};

const kategoriIcons = {
  'Lampu': '💡',
  'Mesin': '🔧',
  'AC': '❄️',
  'Body': '🚗',
  'Elektrik': '⚡',
  'Ban': '🔘',
  'Rem': '🛑',
  'Transmisi': '⚙️',
  'Lainnya': '📋',
};

const emptyForm = {
  vehicle_id: '', no_body: '', no_polisi: '',
  pelapor: '', no_hp: '',
  kategori: 'Lainnya', deskripsi: '',
  prioritas: 'Normal', status: 'Menunggu',
  foto_url: '',
  estimasi_biaya: 0,
};

function SectionHeader({ icon, title, subtitle }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      borderBottom: '2px solid var(--primary)',
      paddingBottom: 8, marginBottom: 16, marginTop: 20,
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8,
        background: 'var(--primary)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff',
      }}>
        {icon}
      </div>
      <div>
        <h4 style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--gray-700)', margin: 0 }}>{title}</h4>
        {subtitle && <small style={{ color: 'var(--gray-400)', fontSize: '0.75rem' }}>{subtitle}</small>}
      </div>
    </div>
  );
}

function LabelInput({ label, required, children }) {
  return (
    <div className="form-group">
      <label>{label}{required && <span style={{ color: '#e74c3c' }}> *</span>}</label>
      {children}
    </div>
  );
}

export default function Ticketing() {
  const { user, hasMenuAction } = useAuth();
  const [data, setData] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterKategori, setFilterKategori] = useState('');
  const [filterPrioritas, setFilterPrioritas] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [stats, setStats] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [vehicleDropdown, setVehicleDropdown] = useState(false);

  // Approve/Reject modal state
  const [approveModal, setApproveModal] = useState(null); // { ticket, action: 'setujui'|'tolak' }
  const [approveData, setApproveData] = useState({ catatan_teknisi: '', estimasi_biaya: 0, rejected_reason: '' });
  const [approveSaving, setApproveSaving] = useState(false);

  // Respond modal (teknisi merespon dengan lokasi)
  const [respondModal, setRespondModal] = useState(null);
  const [respondData, setRespondData] = useState({ lokasi_perbaikan: '', response_notes: '' });
  const [respondSaving, setRespondSaving] = useState(false);

  // Selesai modal
  const [selesaiModal, setSelesaiModal] = useState(null);
  const [selesaiData, setSelesaiData] = useState({ biaya_aktual: 0, catatan_teknisi: '' });
  const [selesaiSaving, setSelesaiSaving] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    Promise.all([getTickets(params), getTicketingVehicles(), getTicketingStats(params)])
      .then(([ticketRes, vehRes, statsRes]) => {
        setData(ticketRes.data || []);
        setVehicles(vehRes.data || []);
        setStats(statsRes.data || null);
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  };

  // Re-fetch when date filters change
  useEffect(() => { load(); }, [startDate, endDate]);

  const filtered = data.filter(d => {
    if (search) {
      const q = search.toLowerCase();
      if (!d.ticket_no?.toLowerCase().includes(q) &&
          !d.no_body?.toLowerCase().includes(q) &&
          !d.no_polisi?.toLowerCase().includes(q) &&
          !d.pelapor?.toLowerCase().includes(q) &&
          !d.deskripsi?.toLowerCase().includes(q)) return false;
    }
    if (filterStatus && d.status !== filterStatus) return false;
    if (filterKategori && d.kategori !== filterKategori) return false;
    if (filterPrioritas && d.prioritas !== filterPrioritas) return false;
    return true;
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, pelapor: user?.full_name || user?.username || '', no_hp: user?.phone || '' });
    setVehicleSearch('');
    setError('');
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      vehicle_id: item.vehicle_id ?? '',
      no_body: item.no_body || '',
      no_polisi: item.no_polisi || '',
      pelapor: item.pelapor || '',
      no_hp: item.no_hp || '',
      kategori: item.kategori || 'Lainnya',
      deskripsi: item.deskripsi || '',
      prioritas: item.prioritas || 'Normal',
      status: item.status || 'Menunggu',
      foto_url: item.foto_url || '',
      estimasi_biaya: item.estimasi_biaya || 0,
    });
    setVehicleSearch(`${item.no_body || ''} — ${item.no_polisi || ''}`);
    setError('');
    setShowModal(true);
  };

  // Filtered vehicles for autocomplete
  const filteredVehicles = vehicles.filter(v => {
    if (!vehicleSearch) return true;
    const q = vehicleSearch.toLowerCase();
    return (v.body_no?.toLowerCase().includes(q) ||
            v.plate_no?.toLowerCase().includes(q) ||
            v.teknisi?.toLowerCase().includes(q));
  }).slice(0, 15);

  const handleVehiclePick = (v) => {
    setForm(f => ({
      ...f,
      vehicle_id: v.id,
      no_body: v.body_no || '',
      no_polisi: v.plate_no || '',
    }));
    setVehicleSearch(`${v.body_no || ''} — ${v.plate_no || ''}`);
    setVehicleDropdown(false);
  };

  const openVehicleDropdown = () => {
    if (!vehicleSearch) setVehicleSearch('');
    setVehicleDropdown(true);
  };

  const handleSave = async () => {
    setError('');
    if (!form.pelapor.trim()) { setError('Nama pelapor harus diisi'); return; }
    if (!form.deskripsi.trim()) { setError('Deskripsi kerusakan harus diisi'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        vehicle_id: form.vehicle_id ? Number(form.vehicle_id) : null,
        estimasi_biaya: Number(form.estimasi_biaya) || 0,
      };
      if (editing) {
        await updateTicket(editing.id, payload);
      } else {
        await createTicket(payload);
      }
      setShowModal(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Yakin ingin menghapus tiket "${item.ticket_no}"?`)) return;
    try { await deleteTicket(item.id); load(); }
    catch (err) { alert(err.response?.data?.error || 'Gagal menghapus'); }
  };

  // Approve/Reject
  const openApprove = (ticket) => {
    setApproveModal(ticket);
    setApproveData({
      catatan_teknisi: ticket.catatan_teknisi || '',
      estimasi_biaya: ticket.estimasi_biaya || 0,
      rejected_reason: '',
    });
  };

  const handleApproveSubmit = async () => {
    setApproveSaving(true);
    try {
      await approveTicket(approveModal.id, {
        action: 'setujui',
        catatan_teknisi: approveData.catatan_teknisi,
        estimasi_biaya: Number(approveData.estimasi_biaya) || 0,
      });
      setApproveModal(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal menyetujui');
    } finally { setApproveSaving(false); }
  };

  const handleRejectSubmit = async () => {
    if (!approveData.rejected_reason.trim()) {
      alert('Alasan penolakan harus diisi');
      return;
    }
    setApproveSaving(true);
    try {
      await approveTicket(approveModal.id, {
        action: 'tolak',
        rejected_reason: approveData.rejected_reason,
      });
      setApproveModal(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal menolak');
    } finally { setApproveSaving(false); }
  };

  // Respond: teknisi mengkonfirmasi dan memberikan lokasi
  const openRespond = (ticket) => {
    setRespondModal(ticket);
    setRespondData({
      lokasi_perbaikan: ticket.lokasi_perbaikan || '',
      response_notes: '',
    });
  };

  const handleRespondSubmit = async () => {
    if (!respondData.lokasi_perbaikan.trim()) {
      alert('Lokasi perbaikan harus diisi');
      return;
    }
    setRespondSaving(true);
    try {
      await respondTicket(respondModal.id, {
        lokasi_perbaikan: respondData.lokasi_perbaikan,
        response_notes: respondData.response_notes,
      });
      setRespondModal(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal mengirim respon');
    } finally { setRespondSaving(false); }
  };

  // Proses (ke "Dalam Perbaikan")
  const handleProses = async (id) => {
    if (!window.confirm('Tandai tiket ini sedang dalam perbaikan?')) return;
    try {
      await prosesTicket(id);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal memproses');
    }
  };

  // Selesai
  const openSelesai = (ticket) => {
    setSelesaiModal(ticket);
    setSelesaiData({
      biaya_aktual: ticket.biaya_aktual || ticket.estimasi_biaya || 0,
      catatan_teknisi: '',
    });
  };

  const handleSelesaiSubmit = async () => {
    setSelesaiSaving(true);
    try {
      await selesaiTicket(selesaiModal.id, {
        biaya_aktual: Number(selesaiData.biaya_aktual) || 0,
        catatan_teknisi: selesaiData.catatan_teknisi,
      });
      setSelesaiModal(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal menyelesaikan');
    } finally { setSelesaiSaving(false); }
  };

  const canCreate = hasMenuAction('ticketing', 'create');
  const canEdit = hasMenuAction('ticketing', 'edit');
  const canDelete = hasMenuAction('ticketing', 'delete');

  const statCards = [
    { label: 'Total Tiket', value: data.length, color: '#11cdef', icon: Hash },
    { label: 'Menunggu', value: data.filter(d => d.status === 'Menunggu').length, color: '#fb6340', icon: Clock },
    { label: 'Disetujui', value: data.filter(d => d.status === 'Disetujui').length, color: '#11cdef', icon: ThumbsUp },
    { label: 'Dalam Perbaikan', value: data.filter(d => d.status === 'Dalam Perbaikan').length, color: '#8965e0', icon: Wrench },
    { label: 'Selesai', value: data.filter(d => d.status === 'Selesai').length, color: '#2dce89', icon: CheckCircle },
    { label: 'Ditolak', value: data.filter(d => d.status === 'Ditolak').length, color: '#8898aa', icon: XCircle },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Wrench size={24} />
              Ticketing — Permintaan Perbaikan
            </h2>
            <p className="page-desc">{data.length} tiket perbaikan kendaraan</p>
          </div>
          {canCreate && (
            <button className="btn btn-primary" onClick={openCreate}
              style={{ background: 'linear-gradient(135deg, #8965e0, #6c4fd1)', border: 'none' }}>
              <Plus size={16} /><span>Buat Tiket Baru</span>
            </button>
          )}
        </div>
      </div>

      {/* Stat Cards */}
      <div className="stat-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
        {statCards.map(s => (
          <div key={s.label} className="stat-card" style={{
            background: '#fff', borderRadius: 12, padding: '14px 16px',
            border: `1px solid ${s.color}20`, borderLeft: `3px solid ${s.color}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <s.icon size={16} color={s.color} />
              <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)', fontWeight: 600, textTransform: 'uppercase' }}>{s.label}</span>
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Dashboard Charts Section */}
      {stats && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <BarChart3 size={18} color="#8965e0" />
            <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: 'var(--gray-700)' }}>
              Dashboard Ticketing
            </h4>
            <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>
              {stats.total_tickets} tiket | Estimasi: Rp {Number(stats.total_estimasi || 0).toLocaleString('id-ID')} | Biaya: Rp {Number(stats.total_biaya || 0).toLocaleString('id-ID')}
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
            {/* Chart 1: Kategori Distribution */}
            {stats.by_kategori?.length > 0 && (
              <div className="chart-card" style={{ padding: 0 }}>
                <div className="chart-card-header" style={{ padding: '12px 16px 0' }}>
                  <h6 className="chart-card-title">Per Kategori</h6>
                  <span className="chart-card-subtitle">Distribusi tiket per kategori kerusakan</span>
                </div>
                <div className="chart-card-body" style={{ padding: '8px 12px' }}>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={stats.by_kategori} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" vertical={false} />
                      <XAxis dataKey="name" stroke="#8898aa" fontSize={10} />
                      <YAxis stroke="#8898aa" fontSize={11} />
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (active && payload?.length) {
                            return (
                              <div className="custom-tooltip">
                                <p className="tooltip-label">{label}</p>
                                <p className="tooltip-item" style={{ color: '#8965e0' }}>
                                  Tiket: <strong>{payload[0].value}</strong>
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar dataKey="count" radius={[6, 6, 0, 0]} barSize={28} fill="#8965e0" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Chart 2: Prioritas Donut */}
            {stats.by_prioritas?.length > 0 && (
              <div className="chart-card" style={{ padding: 0 }}>
                <div className="chart-card-header" style={{ padding: '12px 16px 0' }}>
                  <h6 className="chart-card-title">Per Prioritas</h6>
                  <span className="chart-card-subtitle">Komposisi prioritas tiket</span>
                </div>
                <div className="chart-card-body" style={{ padding: '8px 12px' }}>
                  <div className="donut-wrapper">
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={stats.by_prioritas}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={3}
                          dataKey="count"
                        >
                          {stats.by_prioritas.map((entry, idx) => {
                            const colors = { 'Rendah': '#2dce89', 'Normal': '#11cdef', 'Tinggi': '#fb6340', 'Darurat': '#f5365c' };
                            return <Cell key={idx} fill={colors[entry.name] || '#8898aa'} />;
                          })}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="donut-legend" style={{ flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      {stats.by_prioritas.map((s, i) => {
                        const colors = { 'Rendah': '#2dce89', 'Normal': '#11cdef', 'Tinggi': '#fb6340', 'Darurat': '#f5365c' };
                        return (
                          <div key={i} className="donut-legend-item">
                            <span className="donut-dot" style={{ background: colors[s.name] || '#8898aa' }} />
                            <span>{s.name}: {s.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Chart 3: Teknisi Performance */}
            {stats.by_teknisi?.length > 0 && (
              <div className="chart-card" style={{ padding: 0 }}>
                <div className="chart-card-header" style={{ padding: '12px 16px 0' }}>
                  <h6 className="chart-card-title">Per Teknisi</h6>
                  <span className="chart-card-subtitle">Top 10 teknisi berdasarkan jumlah tiket</span>
                </div>
                <div className="chart-card-body" style={{ padding: '8px 12px' }}>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart
                      data={stats.by_teknisi}
                      layout="vertical"
                      margin={{ top: 5, right: 20, left: 60, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" horizontal={false} />
                      <XAxis type="number" stroke="#8898aa" fontSize={11} />
                      <YAxis type="category" dataKey="name" stroke="#8898aa" fontSize={10} width={55} />
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (active && payload?.length) {
                            return (
                              <div className="custom-tooltip">
                                <p className="tooltip-label">{label}</p>
                                <p className="tooltip-item" style={{ color: '#11cdef' }}>
                                  Tiket: <strong>{payload[0].value}</strong>
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={16} fill="#11cdef" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Chart 4: Monthly Trend */}
            {stats.monthly_trend?.length > 0 && (
              <div className="chart-card" style={{ padding: 0 }}>
                <div className="chart-card-header" style={{ padding: '12px 16px 0' }}>
                  <h6 className="chart-card-title">Tren Bulanan</h6>
                  <span className="chart-card-subtitle">Jumlah tiket per bulan (12 bulan terakhir)</span>
                </div>
                <div className="chart-card-body" style={{ padding: '8px 12px' }}>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={stats.monthly_trend} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" vertical={false} />
                      <XAxis dataKey="month" stroke="#8898aa" fontSize={9} />
                      <YAxis stroke="#8898aa" fontSize={11} allowDecimals={false} />
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (active && payload?.length) {
                            return (
                              <div className="custom-tooltip">
                                <p className="tooltip-label">{label}</p>
                                <p className="tooltip-item" style={{ color: '#8965e0' }}>
                                  Tiket: <strong>{payload[0].value}</strong>
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Line type="monotone" dataKey="count" stroke="#8965e0" strokeWidth={2} dot={{ r: 3, fill: '#8965e0' }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        background: '#fff', padding: '12px 16px', borderRadius: 12,
        border: '1px solid var(--gray-100)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        marginBottom: 20,
      }}>
        <div className="search-box" style={{ flex: 1, minWidth: 200, maxWidth: 320 }}>
          <Search size={16} />
          <input type="text" placeholder="Cari tiket, pelapor, no body..." value={search}
            onChange={e => setSearch(e.target.value)} />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ padding: '8px 10px', border: '1px solid var(--gray-200)', borderRadius: 8, fontSize: '0.82rem', background: '#fff', minWidth: 130 }}>
          <option value="">Semua Status</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filterKategori} onChange={e => setFilterKategori(e.target.value)}
          style={{ padding: '8px 10px', border: '1px solid var(--gray-200)', borderRadius: 8, fontSize: '0.82rem', background: '#fff', minWidth: 120 }}>
          <option value="">Semua Kategori</option>
          {KATEGORI_OPTIONS.map(k => <option key={k} value={k}>{kategoriIcons[k]} {k}</option>)}
        </select>
        <select value={filterPrioritas} onChange={e => setFilterPrioritas(e.target.value)}
          style={{ padding: '8px 10px', border: '1px solid var(--gray-200)', borderRadius: 8, fontSize: '0.82rem', background: '#fff', minWidth: 120 }}>
          <option value="">Semua Prioritas</option>
          {PRIORITAS_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>

        {/* Date Range Filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Calendar size={14} style={{ color: 'var(--gray-400)' }} />
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
            style={{
              padding: '7px 8px', border: '1px solid var(--gray-200)', borderRadius: 8,
              fontSize: '0.78rem', background: '#fff', maxWidth: 140,
              color: startDate ? 'var(--gray-700)' : 'var(--gray-400)',
            }} />
          <span style={{ color: 'var(--gray-300)', fontSize: '0.75rem' }}>s/d</span>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
            style={{
              padding: '7px 8px', border: '1px solid var(--gray-200)', borderRadius: 8,
              fontSize: '0.78rem', background: '#fff', maxWidth: 140,
              color: endDate ? 'var(--gray-700)' : 'var(--gray-400)',
            }} />
        </div>

        {(search || filterStatus || filterKategori || filterPrioritas || startDate || endDate) && (
          <button className="btn-icon" title="Reset filter"
            onClick={() => { setSearch(''); setFilterStatus(''); setFilterKategori(''); setFilterPrioritas(''); setStartDate(''); setEndDate(''); }}
            style={{ color: '#f5365c' }}>
            <X size={16} />
          </button>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="page-loading">Memuat data tiket...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#f9fafb', borderRadius: 16, border: '1px dashed #d1d5db' }}>
          <Wrench size={48} style={{ color: '#9ca3af', marginBottom: 12, opacity: 0.5 }} />
          <h3 style={{ color: '#374151', marginBottom: 6, fontSize: '1.1rem' }}>
            {search ? 'Tiket Tidak Ditemukan' : 'Belum Ada Tiket Perbaikan'}
          </h3>
          <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>
            {search ? 'Coba gunakan kata kunci lain.' : 'Pengemudi/pemilik kendaraan dapat mengajukan tiket perbaikan.'}
          </p>
        </div>
      ) : (
        <div className="regions-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))' }}>
          {filtered.map(d => {
            const sc = statusConfig[d.status] || statusConfig['Menunggu'];
            const pc = prioritasConfig[d.prioritas] || prioritasConfig['Normal'];
            return (
              <div key={d.id} className="region-card" style={{ borderTop: `3px solid ${sc.color}` }}>
                <div className="region-card-header" style={{
                  background: '#fff', borderBottom: '1px solid var(--gray-100)', padding: '14px 16px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 10,
                      background: `linear-gradient(135deg, ${sc.color}, ${pc.color})`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                    }}>
                      <Wrench size={20} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.7rem', fontFamily: 'monospace', fontWeight: 600, color: 'var(--gray-500)' }}>
                          <Hash size={10} style={{ verticalAlign: 'middle', marginRight: 2 }} />
                          {d.ticket_no}
                        </span>
                        <span style={{
                          fontSize: '0.65rem', padding: '2px 8px', borderRadius: 20, fontWeight: 600, ...sc,
                        }}>
                          {d.status}
                        </span>
                        <span style={{
                          fontSize: '0.6rem', padding: '2px 6px', borderRadius: 4, fontWeight: 600, ...pc,
                        }}>
                          {d.prioritas}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                        <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                          {d.no_polisi || '-'} {d.no_body ? `(${d.no_body})` : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="region-card-body" style={{ padding: '12px 16px' }}>
                  <div className="region-detail">
                    <span style={{ fontSize: '0.78rem', marginRight: 6 }}>{kategoriIcons[d.kategori] || '📋'}</span>
                    <strong>Kategori:</strong> {d.kategori}
                  </div>
                  <div className="region-detail"><User size={14} className="text-muted" /><strong>Pelapor:</strong> {d.pelapor}{d.no_hp ? ` (${d.no_hp})` : ''}</div>

                  <div className="region-detail" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <FileText size={14} style={{ color: 'var(--gray-400)' }} />
                      <strong style={{ fontSize: '0.78rem' }}>Deskripsi:</strong>
                    </div>
                    <p style={{ margin: '2px 0 0 20px', fontSize: '0.78rem', color: 'var(--gray-600)', whiteSpace: 'pre-wrap' }}>{d.deskripsi}</p>
                  </div>

                  {/* Estimasi biaya */}
                  {d.estimasi_biaya > 0 && (
                    <div className="region-detail"><DollarSign size={14} className="text-muted" /><strong>Estimasi Biaya:</strong> Rp {Number(d.estimasi_biaya).toLocaleString('id-ID')}</div>
                  )}
                  {d.biaya_aktual > 0 && (
                    <div className="region-detail"><DollarSign size={14} className="text-muted" /><strong>Biaya Aktual:</strong> Rp {Number(d.biaya_aktual).toLocaleString('id-ID')}</div>
                  )}

                  {/* Assigned Teknisi */}
                  {d.assigned_teknisi && (
                    <div className="region-detail">
                      <User size={14} className="text-muted" />
                      <strong>Teknisi:</strong> {d.assigned_teknisi}
                    </div>
                  )}

                  {/* Respons info */}
                  {d.responded_at && (
                    <div className="region-detail">
                      <CheckSquare size={14} style={{ color: '#8965e0' }} />
                      <strong>Respon:</strong> {d.responded_at.slice(0, 16)} — {d.lokasi_perbaikan || '-'}
                    </div>
                  )}
                  {d.response_notes && (
                    <div className="region-detail" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <MessageSquare size={14} style={{ color: 'var(--gray-400)' }} />
                        <strong style={{ fontSize: '0.78rem' }}>Catatan Respon:</strong>
                      </div>
                      <p style={{ margin: '2px 0 0 20px', fontSize: '0.78rem', color: 'var(--gray-600)', whiteSpace: 'pre-wrap' }}>{d.response_notes}</p>
                    </div>
                  )}

                  {/* Completed info */}
                  {d.completed_at && (
                    <div className="region-detail" style={{ color: '#2dce89' }}>
                      <CheckCircle size={14} />
                      <strong>Selesai:</strong> {d.completed_at.slice(0, 16)}
                    </div>
                  )}

                  {/* Catatan teknisi */}
                  {d.catatan_teknisi && (
                    <div className="region-detail" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <MessageSquare size={14} style={{ color: 'var(--gray-400)' }} />
                        <strong style={{ fontSize: '0.78rem' }}>Catatan Teknisi:</strong>
                      </div>
                      <p style={{ margin: '2px 0 0 20px', fontSize: '0.78rem', color: 'var(--gray-600)', whiteSpace: 'pre-wrap' }}>{d.catatan_teknisi}</p>
                    </div>
                  )}

                  {/* Alasan penolakan */}
                  {d.rejected_reason && (
                    <div className="region-detail" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <XCircle size={14} color="#f5365c" />
                        <strong style={{ fontSize: '0.78rem', color: '#f5365c' }}>Alasan Ditolak:</strong>
                      </div>
                      <p style={{ margin: '2px 0 0 20px', fontSize: '0.78rem', color: '#f5365c', whiteSpace: 'pre-wrap' }}>{d.rejected_reason}</p>
                    </div>
                  )}

                  {/* Foto preview */}
                  {d.foto_url && (
                    <div style={{ marginTop: 8 }}>
                      <a href={d.foto_url} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: '#8965e0', textDecoration: 'none', padding: '4px 8px', background: '#f3edff', borderRadius: 6 }}>
                        <Camera size={14} /> Lihat Foto
                      </a>
                    </div>
                  )}

                  {d.cm_id && (
                    <div className="region-detail" style={{ marginTop: 4 }}>
                      <Wrench size={14} className="text-muted" />
                      <strong>CM ID:</strong> #{d.cm_id}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="action-btns" style={{ marginTop: 12, justifyContent: 'flex-end', borderTop: '1px solid var(--gray-100)', paddingTop: 10, gap: 6 }}>
                    {/* Respond with location - for teknisi when 'Menunggu' or 'Disetujui' */}
                    {(d.status === 'Menunggu' || d.status === 'Disetujui') && (
                      <button className="btn-icon" title="Respon & Konfirmasi Lokasi"
                        onClick={() => openRespond(d)}
                        style={{ color: '#8965e0' }}>
                        <MapPin size={14} />
                      </button>
                    )}
                    {/* Approve/Reject only for 'Menunggu' status */}
                    {d.status === 'Menunggu' && (
                      <>
                        <button className="btn-icon" title="Setujui"
                          onClick={() => openApprove(d, 'setujui')}
                          style={{ color: '#2dce89' }}>
                          <ThumbsUp size={14} />
                        </button>
                        <button className="btn-icon" title="Tolak"
                          onClick={() => openApprove(d, 'tolak')}
                          style={{ color: '#f5365c' }}>
                          <ThumbsDown size={14} />
                        </button>
                      </>
                    )}
                    {/* Proses (ke Dalam Perbaikan) - only for 'Disetujui' */}
                    {d.status === 'Disetujui' && (
                      <button className="btn-icon" title="Proses Perbaikan"
                        onClick={() => handleProses(d.id)}
                        style={{ color: '#8965e0' }}>
                        <Wrench size={14} />
                      </button>
                    )}
                    {/* Selesai - only for 'Dalam Perbaikan' */}
                    {d.status === 'Dalam Perbaikan' && (
                      <button className="btn-icon" title="Selesaikan"
                        onClick={() => openSelesai(d)}
                        style={{ color: '#2dce89' }}>
                        <CheckCircle size={14} />
                      </button>
                    )}
                    {/* Edit - only if Menunggu */}
                    {canEdit && (d.status === 'Menunggu' || d.status === 'Disetujui') && (
                      <button className="btn-icon" title="Edit" onClick={() => openEdit(d)}><Edit2 size={14} /></button>
                    )}
                    {canDelete && d.status === 'Menunggu' && (
                      <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(d)}><Trash2 size={14} /></button>
                    )}
                    <span style={{ fontSize: '0.65rem', color: 'var(--gray-400)', marginLeft: 4 }}>
                      {d.created_at?.slice(0, 10)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ====== CREATE/EDIT MODAL ====== */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)} style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1000 }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}
            style={{ maxWidth: 700, width: '95%', maxHeight: '90vh', overflow: 'auto', borderRadius: 12 }}>
            <div className="modal-header" style={{
              background: 'linear-gradient(135deg, #8965e0, #6c4fd1)',
              color: '#fff', borderRadius: '12px 12px 0 0', padding: '16px 24px',
              position: 'sticky', top: 0, zIndex: 10,
            }}>
              <div>
                <h3 style={{ color: '#fff', margin: 0, fontSize: '1.1rem' }}>
                  {editing ? 'Edit Tiket Perbaikan' : 'Buat Tiket Perbaikan'}
                </h3>
                <small style={{ opacity: 0.85 }}>Permintaan perbaikan kendaraan</small>
              </div>
              <button className="btn-icon" onClick={() => setShowModal(false)} style={{ color: '#fff' }}><X size={20} /></button>
            </div>
            <div className="modal-body" style={{ padding: '20px 24px', background: '#fff' }}>
              {error && (
                <div className="form-error" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#fff5f5', border: '1px solid #fecaca', borderRadius: 8, color: '#c0392b' }}>
                  <AlertCircle size={16} /><span>{error}</span>
                </div>
              )}

              <SectionHeader icon={<User size={16} />} title="Informasi Pelapor" subtitle="Data pengemudi/pemilik yang melapor" />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <LabelInput label="Nama Pelapor" required>
                  <input type="text" value={form.pelapor} onChange={e => setForm({ ...form, pelapor: e.target.value })}
                    placeholder="Nama pengemudi/pemilik" style={{ fontSize: '0.82rem' }} />
                </LabelInput>
                <LabelInput label="No. HP">
                  <input type="text" value={form.no_hp} onChange={e => setForm({ ...form, no_hp: e.target.value })}
                    placeholder="Contoh: 0812xxx" style={{ fontSize: '0.82rem' }} />
                </LabelInput>
              </div>

              <SectionHeader icon={<Wrench size={16} />} title="Informasi Kendaraan & Kerusakan" subtitle="Data kendaraan dan detail masalah" />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <LabelInput label="Cari Kendaraan (No. Body)">
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={vehicleSearch}
                      onChange={e => {
                        setVehicleSearch(e.target.value);
                        setVehicleDropdown(true);
                        if (!e.target.value) {
                          setForm(f => ({ ...f, vehicle_id: '', no_body: '', no_polisi: '' }));
                        }
                      }}
                      onFocus={openVehicleDropdown}
                      onBlur={() => setTimeout(() => setVehicleDropdown(false), 200)}
                      placeholder="Ketik No. Body, Polisi, atau Teknisi..."
                      style={{
                        fontSize: '0.82rem', padding: '8px 12px', borderRadius: 8,
                        border: '1px solid var(--gray-200)', background: '#fff', width: '100%',
                      }}
                    />
                    {vehicleDropdown && filteredVehicles.length > 0 && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 280, overflowY: 'auto',
                      }}>
                        {filteredVehicles.map(v => (
                          <div
                            key={v.id}
                            onMouseDown={() => handleVehiclePick(v)}
                            style={{
                              padding: '8px 12px', cursor: 'pointer',
                              borderBottom: '1px solid #f3f4f6',
                              transition: 'background 0.15s',
                            }}
                            onMouseOver={e => e.currentTarget.style.background = '#f5f3ff'}
                            onMouseOut={e => e.currentTarget.style.background = '#fff'}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                {v.body_no || '-'}
                              </span>
                              <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)', fontFamily: 'monospace' }}>
                                {v.plate_no || '-'}
                              </span>
                            </div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)', marginTop: 2 }}>
                              {v.teknisi ? `Teknisi: ${v.teknisi}` : 'Belum ada teknisi'}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {vehicleDropdown && filteredVehicles.length === 0 && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '12px 16px',
                        fontSize: '0.82rem', color: 'var(--gray-400)',
                      }}>
                        Kendaraan tidak ditemukan
                      </div>
                    )}
                  </div>
                </LabelInput>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <LabelInput label="No. Polisi">
                    <input type="text" value={form.no_polisi} onChange={e => setForm({ ...form, no_polisi: e.target.value })}
                      placeholder="Polisi" style={{ fontSize: '0.82rem' }} />
                  </LabelInput>
                  <LabelInput label="No. Body">
                    <input type="text" value={form.no_body} onChange={e => setForm({ ...form, no_body: e.target.value })}
                      placeholder="Body" style={{ fontSize: '0.82rem' }} />
                  </LabelInput>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 4 }}>
                <LabelInput label="Kategori Kerusakan" required>
                  <select value={form.kategori} onChange={e => setForm({ ...form, kategori: e.target.value })}
                    style={{ fontSize: '0.82rem', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--gray-200)', background: '#fff', width: '100%' }}>
                    {KATEGORI_OPTIONS.map(k => <option key={k} value={k}>{kategoriIcons[k]} {k}</option>)}
                  </select>
                </LabelInput>
                <LabelInput label="Prioritas">
                  <select value={form.prioritas} onChange={e => setForm({ ...form, prioritas: e.target.value })}
                    style={{
                      fontSize: '0.82rem', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--gray-200)', background: '#fff', width: '100%',
                      fontWeight: 600, color: (prioritasConfig[form.prioritas] || prioritasConfig['Normal']).color,
                    }}>
                    {PRIORITAS_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </LabelInput>
              </div>

              <div className="form-group" style={{ marginTop: 12 }}>
                <label>Deskripsi Kerusakan <span style={{ color: '#e74c3c' }}>*</span></label>
                <textarea rows={4} value={form.deskripsi}
                  onChange={e => setForm({ ...form, deskripsi: e.target.value })}
                  placeholder="Jelaskan secara detail kerusakan yang dialami kendaraan..."
                  style={{
                    width: '100%', padding: '10px 12px', border: '1px solid var(--gray-200)',
                    borderRadius: 8, fontSize: '0.85rem', resize: 'vertical',
                    fontFamily: 'inherit', lineHeight: 1.6,
                  }} />
              </div>

              <div className="form-group">
                <label>Foto Kerusakan (URL)</label>
                <input type="text" value={form.foto_url} onChange={e => setForm({ ...form, foto_url: e.target.value })}
                  placeholder="URL foto kerusakan (opsional)" />
              </div>

              {/* Show status change only when editing */}
              {editing && (
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label>Status</label>
                  <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                    style={{ fontWeight: 600, color: (statusConfig[form.status] || statusConfig['Menunggu']).color }}>
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div className="modal-footer" style={{
              padding: '12px 24px', borderTop: '1px solid var(--gray-100)',
              display: 'flex', justifyContent: 'flex-end', gap: 10,
              background: '#fafafa', borderRadius: '0 0 12px 12px',
              position: 'sticky', bottom: 0, zIndex: 10,
            }}>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}
                style={{ background: 'linear-gradient(135deg, #8965e0, #6c4fd1)', border: 'none' }}>
                {saving ? 'Menyimpan...' : editing ? 'Perbarui Tiket' : 'Buat Tiket'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====== RESPOND MODAL (Teknisi konfirmasi lokasi) ====== */}
      {respondModal && (
        <div className="modal-overlay" onClick={() => setRespondModal(null)} style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1000 }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}
            style={{ maxWidth: 550, width: '95%', borderRadius: 12 }}>
            <div className="modal-header" style={{
              background: 'linear-gradient(135deg, #8965e0, #6c4fd1)',
              color: '#fff', borderRadius: '12px 12px 0 0', padding: '16px 24px',
            }}>
              <div>
                <h3 style={{ color: '#fff', margin: 0, fontSize: '1.1rem' }}>Respon Tiket Perbaikan</h3>
                <small style={{ opacity: 0.85 }}>{respondModal.ticket_no} — {respondModal.no_polisi}</small>
              </div>
              <button className="btn-icon" onClick={() => setRespondModal(null)} style={{ color: '#fff' }}><X size={20} /></button>
            </div>
            <div className="modal-body" style={{ padding: '20px 24px', background: '#fff' }}>
              <div style={{
                background: '#f3edff', borderRadius: 8, padding: '10px 14px',
                marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <MapPin size={18} color="#8965e0" />
                <span style={{ fontSize: '0.85rem', color: '#5a3d8a', lineHeight: 1.4 }}>
                  Konfirmasi bahwa Anda akan menangani perbaikan ini. Isi lokasi untuk memberi tahu pelapor.
                </span>
              </div>

              <div className="form-group">
                <label>Lokasi Perbaikan <span style={{ color: '#e74c3c' }}>*</span></label>
                <input type="text" value={respondData.lokasi_perbaikan}
                  onChange={e => setRespondData({ ...respondData, lokasi_perbaikan: e.target.value })}
                  placeholder="Contoh: Pool KWK, Jl. Raya No. 123, atau lokasi kendaraan..."
                  style={{ fontSize: '0.85rem' }} />
              </div>
              <div className="form-group">
                <label>Catatan untuk Pelapor</label>
                <textarea rows={3} value={respondData.response_notes}
                  onChange={e => setRespondData({ ...respondData, response_notes: e.target.value })}
                  placeholder="Informasi tambahan untuk pelapor (opsional)"
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--gray-200)', borderRadius: 8, fontSize: '0.85rem', resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
            </div>
            <div className="modal-footer" style={{
              padding: '12px 24px', borderTop: '1px solid var(--gray-100)',
              display: 'flex', justifyContent: 'flex-end', gap: 10,
              background: '#fafafa', borderRadius: '0 0 12px 12px',
            }}>
              <button className="btn btn-secondary" onClick={() => setRespondModal(null)}>Batal</button>
              <button className="btn btn-primary" onClick={handleRespondSubmit} disabled={respondSaving}
                style={{ background: 'linear-gradient(135deg, #8965e0, #6c4fd1)', border: 'none' }}>
                {respondSaving ? 'Mengirim...' : 'Konfirmasi & Respon'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====== APPROVE/REJECT MODAL ====== */}
      {approveModal && (
        <div className="modal-overlay" onClick={() => setApproveModal(null)} style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1000 }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}
            style={{ maxWidth: 550, width: '95%', borderRadius: 12 }}>
            <div className="modal-header" style={{
              background: 'linear-gradient(135deg, #8965e0, #6c4fd1)',
              color: '#fff', borderRadius: '12px 12px 0 0', padding: '16px 24px',
            }}>
              <div>
                <h3 style={{ color: '#fff', margin: 0, fontSize: '1.1rem' }}>
                  {approveData.rejected_reason === undefined ? 'Setujui Tiket' : 'Konfirmasi Penolakan'}
                </h3>
                <small style={{ opacity: 0.85 }}>{approveModal.ticket_no} — {approveModal.no_polisi}</small>
              </div>
              <button className="btn-icon" onClick={() => setApproveModal(null)} style={{ color: '#fff' }}><X size={20} /></button>
            </div>
            <div className="modal-body" style={{ padding: '20px 24px', background: '#fff' }}>
              {/* Quick actions: setujui/tolak toggle */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                <button onClick={() => setApproveData(prev => ({ ...prev, rejected_reason: undefined }))}
                  style={{
                    flex: 1, padding: '10px', borderRadius: 8, border: approveData.rejected_reason === undefined ? '2px solid #2dce89' : '1px solid var(--gray-200)',
                    background: approveData.rejected_reason === undefined ? '#e8f8f0' : '#fff',
                    cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', color: '#2dce89',
                  }}>
                  <ThumbsUp size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Setujui
                </button>
                <button onClick={() => setApproveData(prev => ({ ...prev, rejected_reason: '' }))}
                  style={{
                    flex: 1, padding: '10px', borderRadius: 8,
                    border: approveData.rejected_reason === '' ? '2px solid #f5365c' : '1px solid var(--gray-200)',
                    background: approveData.rejected_reason === '' ? '#fff5f5' : '#fff',
                    cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', color: '#f5365c',
                  }}>
                  <ThumbsDown size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Tolak
                </button>
              </div>

              {approveData.rejected_reason === undefined ? (
                /* FORM SETUJUI */
                <>
                  <SectionHeader icon={<CheckCircle size={16} />} title="Setujui Tiket" subtitle="Isi estimasi biaya dan catatan teknisi" />
                  <div className="form-group">
                    <label>Estimasi Biaya (Rp)</label>
                    <input type="number" value={approveData.estimasi_biaya}
                      onChange={e => setApproveData({ ...approveData, estimasi_biaya: e.target.value })}
                      placeholder="0" min="0" />
                  </div>
                  <div className="form-group">
                    <label>Catatan Teknisi</label>
                    <textarea rows={3} value={approveData.catatan_teknisi}
                      onChange={e => setApproveData({ ...approveData, catatan_teknisi: e.target.value })}
                      placeholder="Catatan dari teknisi (opsional)"
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--gray-200)', borderRadius: 8, fontSize: '0.85rem', resize: 'vertical', fontFamily: 'inherit' }} />
                  </div>
                  <div className="modal-footer" style={{ padding: '12px 0 0', borderTop: '1px solid var(--gray-100)', marginTop: 16 }}>
                    <button className="btn btn-secondary" onClick={() => setApproveModal(null)}>Batal</button>
                    <button className="btn btn-primary" onClick={handleApproveSubmit} disabled={approveSaving}
                      style={{ background: 'linear-gradient(135deg, #2dce89, #1aae6f)', border: 'none' }}>
                      {approveSaving ? 'Menyetujui...' : 'Setujui Tiket'}
                    </button>
                  </div>
                </>
              ) : (
                /* FORM TOLAK */
                <>
                  <SectionHeader icon={<XCircle size={16} />} title="Tolak Tiket" subtitle="Isi alasan penolakan" />
                  <div className="form-group">
                    <label>Alasan Penolakan <span style={{ color: '#e74c3c' }}>*</span></label>
                    <textarea rows={3} value={approveData.rejected_reason}
                      onChange={e => setApproveData({ ...approveData, rejected_reason: e.target.value })}
                      placeholder="Jelaskan alasan mengapa tiket ditolak..."
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--gray-200)', borderRadius: 8, fontSize: '0.85rem', resize: 'vertical', fontFamily: 'inherit' }} />
                  </div>
                  <div className="modal-footer" style={{ padding: '12px 0 0', borderTop: '1px solid var(--gray-100)', marginTop: 16 }}>
                    <button className="btn btn-secondary" onClick={() => setApproveModal(null)}>Batal</button>
                    <button className="btn btn-danger" onClick={handleRejectSubmit} disabled={approveSaving}
                      style={{ background: 'linear-gradient(135deg, #f5365c, #d63031)', border: 'none', color: '#fff' }}>
                      {approveSaving ? 'Menolak...' : 'Tolak Tiket'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ====== SELESAI MODAL ====== */}
      {selesaiModal && (
        <div className="modal-overlay" onClick={() => setSelesaiModal(null)} style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1000 }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}
            style={{ maxWidth: 500, width: '95%', borderRadius: 12 }}>
            <div className="modal-header" style={{
              background: 'linear-gradient(135deg, #2dce89, #1aae6f)',
              color: '#fff', borderRadius: '12px 12px 0 0', padding: '16px 24px',
            }}>
              <div>
                <h3 style={{ color: '#fff', margin: 0, fontSize: '1.1rem' }}>Selesaikan Tiket</h3>
                <small style={{ opacity: 0.85 }}>{selesaiModal.ticket_no} — {selesaiModal.no_polisi}</small>
              </div>
              <button className="btn-icon" onClick={() => setSelesaiModal(null)} style={{ color: '#fff' }}><X size={20} /></button>
            </div>
            <div className="modal-body" style={{ padding: '20px 24px', background: '#fff' }}>
              <div className="form-group">
                <label>Biaya Aktual (Rp)</label>
                <input type="number" value={selesaiData.biaya_aktual}
                  onChange={e => setSelesaiData({ ...selesaiData, biaya_aktual: e.target.value })}
                  placeholder="0" min="0" />
              </div>
              <div className="form-group">
                <label>Catatan Teknisi</label>
                <textarea rows={3} value={selesaiData.catatan_teknisi}
                  onChange={e => setSelesaiData({ ...selesaiData, catatan_teknisi: e.target.value })}
                  placeholder="Hasil perbaikan, tindakan yang dilakukan..."
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--gray-200)', borderRadius: 8, fontSize: '0.85rem', resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
            </div>
            <div className="modal-footer" style={{
              padding: '12px 24px', borderTop: '1px solid var(--gray-100)',
              display: 'flex', justifyContent: 'flex-end', gap: 10,
              background: '#fafafa', borderRadius: '0 0 12px 12px',
            }}>
              <button className="btn btn-secondary" onClick={() => setSelesaiModal(null)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSelesaiSubmit} disabled={selesaiSaving}
                style={{ background: 'linear-gradient(135deg, #2dce89, #1aae6f)', border: 'none' }}>
                {selesaiSaving ? 'Menyelesaikan...' : 'Selesaikan Tiket'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
